import { Lightning as lng, Registry, Utils } from '@lightningjs/sdk';
import { SearchPageDataSource } from './business/SearchPageDataSource';
import { AtvKeyboardInput } from '../../../components/keyboard/AtvKeyboardInput';
import { AtvKeyboardService } from '../../../components/keyboard/AtvKeyboardService';
import { debounced } from '../../../Business/utility/Debounced';
import { CmsConstants } from '../../../Business/cms/CmsConstants';
import { ScreenSize } from '../../../api/ScreenSize';
import { ScrollList } from '../../../components/shared/ScrollList';
import { SearchCompletionItem } from './Component/SearchCompletionItem';
import { Spinner } from '../../../components/shared/Spinner';
import { ErrorTranslationKeys } from '../../../api/models/TranslationKeys';
import { _constants } from '../../../components/MiniDetail';
import { Theme } from '../../../config/theme';

const constants = {
  marginLeft: 147,
  marginTop: 40,
  Rows: { w: ScreenSize.w },
  SearchBar: { h: 60 },
  Content: { h: 960 },
};

export class SearchPage extends lng.Component {
  static _template() {
    return {
      x: 0,
      y: 0,
      w: w => w,
      h: h => h,
      Background: {
        visible: false,
        w: w => w,
        h: h => h,
      },

      Spinner: {
        visible: false,
        x: ScreenSize.w / 2,
        y: ScreenSize.h / 2,
        mountX: 0.5,
        type: Spinner,
      },

      Noresults: {
        visible: false,
        x: ScreenSize.w / 2,
        y: ScreenSize.h / 2,
        mountX: 0.5,
        text: {
          text: '',
          ...Theme.Fonts.Default,
        },
      },

      Content: {
        visible: false,
        x: constants.marginLeft,

        SearchBox: {
          y: constants.marginTop,

          Icon: {
            src: Utils.asset('images/menu/SEARCH_ICON.png'),
            visible: true,
            w: constants.SearchBar.h,
            h: constants.SearchBar.h,
          },
          SearchInputField: {
            x: constants.SearchBar.h,
            h: constants.SearchBar.h,
            w: 1677,
            type: AtvKeyboardInput,
            signals: {},
            inputColors: {
              focus: 0x00000000,
              unfocus: 0x00000000,
            },
            textColor: 0xffffffff,
            cursorColor: 0xffffffff,
          },

          ScrollClip: {
            x: -constants.marginLeft,
            clipping: true,
            y: 80,
            h: constants.Content.h,
            w: constants.Rows.w,
            Scroll: {
              Completions: {
                w: constants.Rows.w - constants.marginLeft,
                x: constants.marginLeft,
                type: ScrollList,
                direction: 'row',
                peekNext: true,
              },

              Rows: {
                y: this.bindProp('completionItemHeight'),
                w: constants.Rows.w,
                flex: { direction: 'column' },
              },
            },
          },
        },
      },
    };
  }

  _init() {
    this.completionItemHeight = 0;
    if (this.params.fullScreen) {
      // set initial background when fullscreen
      this.tag('Background').visible = true;
      this.tag('Background').src = Utils.asset('images/background.png');
    }

    this.dataSource = new SearchPageDataSource(this.params.backend, this.params.menuItem);
    this.dataSource.init().then(() => {
      this.tag('Content').visible = true;
      this._refocus();
    });

    this.handleInput = debounced((...args) => this._handleInput(...args), 2500);
    this.tag('SearchInputField').signals.onChanged = (e, result) => {
      this.handleInput(result.input);
    };

    this.focusIdx = -1;
    this.prevSearch = '';

    this.tag('Noresults').text.text = this.params.backend.translations.forKey(
      ErrorTranslationKeys.error_search_no_results
    );
    this.blockLeft = true;
    this._firstOpen = true;
  }

  _handleInput(text) {
    if (text.length < 3) return;
    if (text.toLowerCase() === this.prevSearch) {
      return;
    }
    this.tag('Rows').childList.clear();
    this.tag('Completions').clearItems();
    this.tag('Spinner').visible = true;
    this.prevSearch = text.toLowerCase();
    this.dataSource.search(text).then(result => {
      // completions
      result.completions.forEach(c => {
        this.tag('Completions').items.childList.a({
          flexItem: { marginLeft: 15 },
          backend: this.params.backend,
          type: SearchCompletionItem,
          data: c,
          signals: {
            completionPressed: this.completionPressed.bind(this),
          },
        });
      });

      if (result.completions.length > 0) {
        this.completionItemHeight = 100;
      } else {
        this.completionItemHeight = 0;
      }

      this.tag('Noresults').visible = result.rows.length === 0;

      // rows
      const content = this.tag('Rows');
      result.rows.forEach(ds => {
        ds.load().then(() => {
          content.childList.a({
            flexItem: { marginBottom: 20 },
            signals: { centerVertical: '_centerVertical' },
            w: this.finalW,
            contentOffset: CmsConstants.Ribbon.pageContentOffset,
            ...ds.componentDescription(),
          });
        });
      });
      this.tag('Spinner').visible = false;
      this._refocus();
    });
  }

  completionPressed(val) {
    const input = this.tag('SearchInputField').inputField;
    input.onInputChanged({ input: val });
    // when changing value, reset input field to remove cursor
    this.tag('SearchInputField').reset();
    // do not go via the debounced call
    this._handleInput(val);
  }

  _handleDown() {
    this.focusIdx = Math.min(++this.focusIdx, this.focusable.length - 1);
  }

  _handleUp() {
    this.focusIdx = Math.max(--this.focusIdx, 0);
    if (this.focusIdx <= 1) {
      this.tag('Scroll').setSmooth('y', 0);
    }
  }

  _handleBack() {
    if (this.params.fullScreen) {
      this.fireAncestors('$closeModal');
      return true;
    }
    this.fireAncestors('$focusMenu');
    return true;
  }

  get focusable() {
    const completions = this.tag('Completions');
    if (completions.items.childList.length > 0) {
      return [this.tag('SearchInputField'), completions, ...this.tag('Rows').children];
    } else {
      // if there are no search complete items, don't give focus
      return [this.tag('SearchInputField'), ...this.tag('Rows').children];
    }
  }

  _getFocused() {
    if (this.focusIdx === -1) return undefined;
    if (AtvKeyboardService.instance && AtvKeyboardService.instance.isOpen) {
      return AtvKeyboardService.instance.keyboard;
    }
    return this.tag('Content').visible ? this.focusable[this.focusIdx] : undefined;
  }

  _centerVertical(centerY) {
    // Workaround for modals
    // Showing/hiding a modal will cause a refocus event, at which point this.finalH === 0
    // Prevent recentering in that case
    if (this.finalH === 0) return;

    // const target = this.finalH / 2;
    let target = 1080 / 2;

    const extraOffset = 50;
    if (this.focusable[this.focusIdx].data.cmsProperties) {
      if (!this.focusable[this.focusIdx].data.cmsProperties.includes('HIDE_MINI_DETAIL')) {
        target = (1080 - _constants.miniDetailHeight - extraOffset) / 2;
      }
    } else {
      target = (1080 - _constants.miniDetailHeight - extraOffset) / 2;
    }

    const result = Math.min(
      0, //-this.tag('Content').finalH + this.tag('Content').finalH - constants.marginBottom,
      target - centerY
    );
    // console.log('centerVertical', centerY, target, result, this.tag('Content').finalH);
    // result - 100 te compensate for Completion item height
    this.tag('Scroll').setSmooth('y', result - this.completionItemHeight);
  }

  _detach() {
    AtvKeyboardService.removeInstance();
  }

  _handleLeft() {
    return !!this.blockLeft;
  }

  _active() {
    this.dataSource.logPageView();
    if (this._firstOpen) {
      this._firstOpen = false;
      this.focusIdx = 0;
      this._refocus();
      Registry.setTimeout(() => {
        this.tag('SearchInputField')._handleEnter();
        this._refocus();
        this.blockLeft = false;
      }, 200); // need delay to set focus on the input field
    }
  }

  _focus() {
    // need to use focus to create keyboard because keyboard removes on unfocus and need a new keyboard when widget (a modal) comes back to page
    AtvKeyboardService.init(this);
  }

  _unfocus() {
    // need to use unfocus event because inactive does not trigger when showing a router widget (a modal)
    AtvKeyboardService.removeInstance();
  }
}
